import { HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ApiService } from '@core/providers/http';
import { map, Observable, tap } from 'rxjs';
import {
  CriarDocumentoRequestDto,
  FeClienteSaldoEmDivida,
  FeDocumento,
  FeDocumentoFiltros,
  FePagedResponse,
  FePagedResult,
  FeProduto,
  FeReciboItem,
  FeSaldoDocumento,
  FeSerie,
  FeTaxCode,
  FeTaxExemptionCode,
  FeTipoDocumento,
  FormatoDocumentoPdf,
  SimularPagamentoRequestDto,
  SolicitarSerieRequestDto,
} from '../models/faturacao-eletronica.model';

interface RawDocumentosObject {
  items?: FeDocumento[];
  data?: FeDocumento[];
  results?: FeDocumento[];
  totalRecords?: number;
  totalPages?: number;
  page?: number;
  pageSize?: number;
}

type RawDocumentosResponse = FeDocumento[] | RawDocumentosObject;

@Injectable({ providedIn: 'root' })
export class FaturacaoEletronicaService {
  private readonly api = inject(ApiService);
  private readonly base = '/api/fe';

  /**
   * Signal reactivo que armazena o número total de faturas/documentos fiscais.
   * Utilizado pelo menu lateral (sidebar) e componentes da aplicação.
   */
  readonly totalDocumentos = signal<number>(0);

  /**
   * Obtém a contagem total de documentos fiscais/faturas.
   */
  obterTotalDocumentos(): Observable<number> {
    return this.listarDocumentos({ page: 1, pageSize: 1 }).pipe(
      map((res) => {
        const total = res.totalRecords ?? 0;
        this.totalDocumentos.set(total);
        return total;
      }),
    );
  }

  /**
   * Lista documentos fiscais com paginação e filtros.
   * Suporta filtros por número de documento, tipo, estado, cliente e datas no formato MM-DD-YYYY.
   */
  listarDocumentos(filtros: FeDocumentoFiltros = {}): Observable<FePagedResult<FeDocumento>> {
    const params = this.buildDocumentQueryParams(filtros);
    return this.api
      .get<RawDocumentosResponse>(`${this.base}/documentos`, params)
      .pipe(
        map((res) => this.normalizeDocumentosResult(res, filtros.page || filtros.Page || 1, filtros.pageSize || filtros.PageSize || 10)),
        tap((res) => {
          const isSemFiltros =
            !filtros.estado &&
            !filtros.Estado &&
            !filtros.documentNo &&
            !filtros.DocumentNo &&
            !filtros.idCliente &&
            !filtros.IdCliente &&
            !filtros.dataInicial &&
            !filtros.dataFinal;
          if (isSemFiltros && typeof res.totalRecords === 'number') {
            this.totalDocumentos.set(res.totalRecords);
          }
        }),
      );
  }

  /**
   * Obtém os detalhes completos de um documento por ID.
   */
  obterDocumento(id: number): Observable<FeDocumento> {
    return this.api.get<FeDocumento>(`${this.base}/documentos/${id}`);
  }

  /**
   * Guarda um novo documento fiscal (fica no estado inicial 'Guardada').
   */
  criarDocumento(dto: CriarDocumentoRequestDto): Observable<FeDocumento> {
    return this.api.post<FeDocumento>(`${this.base}/documentos`, dto).pipe(
      tap(() => {
        this.totalDocumentos.update((curr) => curr + 1);
      }),
    );
  }

  /**
   * Atualiza um documento fiscal existente (só permitido no estado 'Guardada').
   */
  atualizarDocumento(id: number, dto: CriarDocumentoRequestDto): Observable<FeDocumento> {
    return this.api.put<FeDocumento>(`${this.base}/documentos/${id}`, dto);
  }

  /**
   * Submete o documento à AGT (passa do estado 'Guardada' para 'Submetida').
   */
  submeterDocumento(id: number): Observable<FeDocumento> {
    return this.api.post<FeDocumento>(`${this.base}/documentos/${id}/submeter`);
  }

  /**
   * Consulta o estado atualizado do documento junto da AGT (passa para 'Aceite' ou 'Rejeitada').
   */
  consultarEstado(id: number): Observable<FeDocumento> {
    return this.api.get<FeDocumento>(`${this.base}/documentos/${id}/estado`);
  }

  /**
   * Cria um novo documento corretivo a partir de um documento rejeitado.
   */
  corrigirDocumento(id: number, dto?: CriarDocumentoRequestDto): Observable<FeDocumento> {
    return this.api.post<FeDocumento>(`${this.base}/documentos/${id}/corrigir`, dto ?? {});
  }

  /**
   * Descarrega o PDF oficial certificado do documento.
   */
  descarregarPdf(id: number, formato: FormatoDocumentoPdf = FormatoDocumentoPdf.A4): Observable<Blob> {
    const params = new HttpParams().set('formato', String(formato));
    return this.api.get<Blob>(`${this.base}/documentos/${id}/pdf`, params, { responseType: 'blob' });
  }

  /**
   * Coleções auxiliares
   */
  listarTiposDocumento(): Observable<FeTipoDocumento[]> {
    return this.api.get<FeTipoDocumento[]>(`${this.base}/tipos-documento`).pipe(
      map((res) => (Array.isArray(res) ? res : ((res as any)?.data || []))),
    );
  }

  listarProdutos(): Observable<FeProduto[]> {
    return this.api.get<FeProduto[]>(`${this.base}/produtos`).pipe(
      map((res) => (Array.isArray(res) ? res : ((res as any)?.data || []))),
    );
  }

  listarTaxCodes(): Observable<FeTaxCode[]> {
    return this.api.get<FeTaxCode[]>(`${this.base}/tax-codes`).pipe(
      map((res) => (Array.isArray(res) ? res : ((res as any)?.data || []))),
    );
  }

  listarIsencoes(): Observable<FeTaxExemptionCode[]> {
    return this.api.get<FeTaxExemptionCode[]>(`${this.base}/tax-exemption-codes`).pipe(
      map((res) => (Array.isArray(res) ? res : ((res as any)?.data || []))),
    );
  }

  listarSeries(): Observable<FeSerie[]> {
    return this.api.get<FeSerie[]>(`${this.base}/series`).pipe(
      map((res) => (Array.isArray(res) ? res : ((res as any)?.data || []))),
    );
  }

  solicitarSerie(dto: SolicitarSerieRequestDto): Observable<unknown> {
    return this.api.post(`${this.base}/series/solicitar`, dto);
  }

  removerSerie(id: number): Observable<void> {
    return this.api.delete<void>(`${this.base}/series/${id}`);
  }

  /**
   * Endpoints de Pagamentos (contrato real do Swagger)
   */
  obterSaldoDocumento(documentId: number): Observable<FeSaldoDocumento> {
    return this.api.get<FeSaldoDocumento>(`${this.base}/pagamentos/documentos/${documentId}/saldo`);
  }

  gerarReferenciaPagamento(documentId: number): Observable<unknown> {
    return this.api.post(`${this.base}/pagamentos/documentos/${documentId}/referencia`);
  }

  simularPagamento(documentId: number, dto: SimularPagamentoRequestDto): Observable<unknown> {
    return this.api.post(`${this.base}/pagamentos/documentos/${documentId}/simular-pagamento`, dto);
  }

  listarRecibos(idFatura: number): Observable<FeReciboItem[]> {
    return this.api.get<FeReciboItem[]>(`${this.base}/pagamentos/documentos/${idFatura}/recibos`).pipe(
      map((res) => (Array.isArray(res) ? res : ((res as any)?.data || []))),
    );
  }

  obterSaldoCliente(idCliente: number): Observable<FeClienteSaldoEmDivida> {
    return this.api.get<FeClienteSaldoEmDivida>(`${this.base}/pagamentos/clientes/${idCliente}/saldo-em-divida`);
  }

  listarFaturasPagasCliente(clientId: number, page = 1, pageSize = 10): Observable<unknown> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    return this.api.get(`${this.base}/pagamentos/clientes/${clientId}/faturas-pagas`, params);
  }

  obterProdutosFatura(idDocumento: number): Observable<FeProduto[]> {
    return this.api.get<FeProduto[]>(`${this.base}/pagamentos/faturas/${idDocumento}/produtos`).pipe(
      map((res) => (Array.isArray(res) ? res : ((res as any)?.data || []))),
    );
  }

  /**
   * Utilitário para forçar o download de ficheiro PDF no browser
   */
  salvarFicheiroPdf(blob: Blob, nomeFicheiro: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeFicheiro.endsWith('.pdf') ? nomeFicheiro : `${nomeFicheiro}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Normalização flexível da resposta de listagem para compatibilidade total
   */
  private normalizeDocumentosResult(
    raw: RawDocumentosResponse,
    fallbackPage: number,
    fallbackPageSize: number,
  ): FePagedResult<FeDocumento> {
    if (!raw) {
      return { items: [], page: fallbackPage, pageSize: fallbackPageSize, totalRecords: 0, totalPages: 0 };
    }

    if (Array.isArray(raw)) {
      return {
        items: raw,
        page: fallbackPage,
        pageSize: fallbackPageSize,
        totalRecords: raw.length,
        totalPages: Math.ceil(raw.length / fallbackPageSize) || 1,
      };
    }

    const items = raw.items || raw.data || raw.results || [];
    const totalRecords = typeof raw.totalRecords === 'number' ? raw.totalRecords : items.length;
    const pageSize = raw.pageSize || fallbackPageSize || 10;
    const page = raw.page || fallbackPage || 1;
    const totalPages = raw.totalPages || Math.ceil(totalRecords / pageSize) || 1;

    return {
      items,
      page,
      pageSize,
      totalRecords,
      totalPages,
    };
  }

  /**
   * Constrói HttpParams garantindo que datas vão no formato MM-DD-YYYY esperado pelo backend
   */
  private buildDocumentQueryParams(f: FeDocumentoFiltros): HttpParams {
    let params = new HttpParams();

    const docNo = f.DocumentNo || f.documentNo;
    if (docNo?.trim()) params = params.set('DocumentNo', docNo.trim());

    const estado = f.Estado || f.estado;
    if (estado && estado !== 'Todas') params = params.set('Estado', estado);

    const idCliente = f.IdCliente ?? f.idCliente;
    if (idCliente !== null && idCliente !== undefined) params = params.set('IdCliente', String(idCliente));

    const idTipoDoc = f.IdTipoDocumento ?? f.idTipoDocumento;
    if (idTipoDoc !== null && idTipoDoc !== undefined) params = params.set('IdTipoDocumento', String(idTipoDoc));

    const reqId = f.RequestId || f.requestId;
    if (reqId?.trim()) params = params.set('RequestId', reqId.trim());

    const dIni = f.DataInicial || f.dataInicial;
    if (dIni) params = params.set('DataInicial', this.formatDateForApi(dIni));

    const dFim = f.DataFinal || f.dataFinal;
    if (dFim) params = params.set('DataFinal', this.formatDateForApi(dFim));

    const page = f.Page ?? f.page ?? 1;
    params = params.set('Page', String(page));

    const pageSize = f.PageSize ?? f.pageSize ?? 10;
    params = params.set('PageSize', String(pageSize));

    return params;
  }

  /**
   * Formata data para MM-DD-YYYY conforme especificação da AGT/Backend
   */
  private formatDateForApi(value: string | Date): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return String(value);

    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  }
}
